import { faAlarmClock, faArrowsToCircle, faBallotCheck, faBuilding, faCalendarClock, faCamera, faCircleEuro, faCircleInfo, faCreditCard, faEuroSign, faFileChartColumn, faFileInvoice, faFileInvoiceDollar, faFileLines, faHouseCrack, faInputNumeric, faInputText, faListCheck, faListOl, faListUl, faMoneyBill, faPencil, faPhoneArrowRight, faPlug, faRadar, faRectangleList, faRepeat, faScrewdriverWrench, faShieldExclamation, faSignature, faSquare, faTag, faTally, faTerminal, faThumbsUp, faTrafficLight, faTriangleExclamation, faWind, faXmarkToSlot, } from '@fortawesome/pro-light-svg-icons';
import _ from 'lodash';
import { z } from 'zod';
import { toHumanReadable } from '../tools/datetime.js';
import { formatEur } from '../tools/number-format.js';
import { zEnumFromArray } from '../tools/zod.js';
export const DocumentClassificationCommonPropertiesConfig = {
    Zusatzkennzeichnung: {
        type: 'text',
        icon: faTag,
        required: false,
        hints: false,
        summary: { label: (value) => value, position: Number.MAX_SAFE_INTEGER },
    },
    Folgeseite: {
        type: 'choice',
        icon: faFileLines,
        required: false,
        options: [
            ['Ja', 'Ja'],
            ['Nein', 'Nein'],
        ],
        multi: false,
        hints: 'Gibt an, ob das Dokument eine Folgeseite, d.h. die zweite, dritte oder spätere Seite zu einem Dokument ist und die erste Seite in diesem konkreten Dokument nicht enthalten ist.',
        summary: false,
    },
    Entwurf: {
        type: 'choice',
        icon: faPencil,
        required: false,
        options: [
            ['Ja', 'Ja'],
            ['Nein', 'Nein'],
        ],
        multi: false,
        hints: false,
        summary: { label: ([value, _acronym]) => (value === 'Ja' ? 'Entwurf' : false), position: 0 },
    },
};
export const DocumentClassificationConfig = {
    // EINGANG
    Abtretungserklärung: {
        acronym: 'AE',
        hints: 'Kennzeichnet Abtretungsformulare von Handwerkern, jedoch nicht Rechnungen mit enthaltener Abtretung.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Firmenname: {
                type: 'text',
                icon: faInputText,
                required: true,
                hints: 'Juristisch korrekter Name des Unternehmens oder der Firma, die nach erfolgerAbtretung die Rechnungsbeträge erhalten wird.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 1 },
            },
            Unterschrieben: {
                type: 'choice',
                icon: faSignature,
                required: true,
                options: [
                    ['Ja', 'Ja'],
                    ['Nein', 'Nein'],
                ],
                multi: false,
                hints: 'Gibt an, ob das Dokument unterschrieben ist.',
                summary: {
                    label: ([value, _acronym]) => (value === 'Ja' ? 'unterz' : false),
                    position: 2,
                },
            },
            IBAN: {
                type: 'text',
                icon: faCreditCard,
                required: false,
                hints: 'IBAN des Kontos, auf das die Rechnungsbeträge nach erfolger Abtretung überwiesen werden sollen.',
                summary: false,
            },
        },
    },
    Abwesenheitsbenachrichtigung: {
        acronym: 'ABW',
        hints: 'Kennzeichnet automatisch erzeugte Abwesenheitsnotizen oder Außer-Haus-Nachrichten, die ausschließlich Abwesenheits-, Kontakt- oder Vertretungsinformationen enthalten, wie sie beispielsweise bei Dienstreisen oder im Urlaub vorkommen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Arbeitsrapport: {
        acronym: 'ARN',
        hints: 'Kennzeichnet meist handschriftlich ausgefüllte Stundenzettel von Handwerkern, die Details zu Arbeitszeiten, Arbeitsschritten und Materialeinsatz enthalten.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Gegenstand: {
                type: 'choice',
                icon: faBallotCheck,
                required: false,
                options: [
                    ['Ursache', 'Urs'],
                    ['Folge', 'Folge'],
                ],
                multi: true,
                hints: 'Gibt an, ob es Maßnahmen zum ursächlichen Schaden oder zu einem Folgeschaden sind.',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 1,
                },
            },
            Gewerke: {
                type: 'choice',
                icon: faSquare,
                required: true,
                options: [
                    ['Leckortung', 'Lo'],
                    ['Notdienst', 'Not'],
                ],
                multi: true,
                hints: 'Liste der auf dem Arbeitsrapport angegebenen Gewerke.',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 2,
                },
            },
        },
    },
    'Anwalts- / Gerichtsschreiben': {
        acronym: 'RA',
        hints: 'Kennzeichnet Briefe von Anwälten oder Gerichten, die keine Kostennote oder Deckungsanfrage darstellen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Frist: {
                type: 'date',
                icon: faCalendarClock,
                required: true,
                hints: 'Datum, bis zu dem eine Antwort oder Maßnahme erforderlich ist.',
                summary: { label: (value) => toHumanReadable(value, 'date_short_2digit'), position: 1 },
            },
            Sachlage: {
                type: 'choice',
                icon: faAlarmClock,
                required: false,
                options: [
                    ['Klageandrohung', 'Klage'],
                    ['Widerspruch', 'Ws'],
                ],
                multi: false,
                hints: 'Gibt an, ob es sich um eine Klageandrohung oder einen Widerspruch handelt.',
                summary: { label: ([_value, acronym]) => acronym, position: 2 },
            },
        },
    },
    Asservat: {
        acronym: 'ASS',
        hints: 'Kennzeichnet Aufkleber mit einer Nummer, die üblicherweise auf einem Begleitbrief angebracht sind und die Verwahrung oder Archivierung eines Beweismittels zum Schadenfall, oft inklusive einer Zustandsbeschreibung, belegen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Nummer: {
                type: 'text',
                icon: faInputNumeric,
                required: true,
                hints: 'Aufgeklebte Nummer',
                summary: { label: (value) => value, position: 1 },
            },
            Gegenstand: {
                type: 'text',
                icon: faTerminal,
                required: false,
                hints: 'Beschreibung des gesicherten Gegenstands oder Beweismittels.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 2 },
            },
        },
    },
    Beschwerde: {
        acronym: 'BS',
        hints: 'Kennzeichnet E-Mails oder Briefe, die mithilfe von Schlüsselwörtern wie "Beschwerde" oder "Beschwerdeverfahren" auf eine langwierige Schadenbearbeitung oder negative Entscheidungen hinweisen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Frist: {
                type: 'date',
                icon: faCalendarClock,
                required: true,
                hints: 'Datum, bis wann die Beschwerde beantwortet oder bearbeitet werden soll.',
                summary: { label: (value) => toHumanReadable(value, 'date_short_2digit'), position: 1 },
            },
        },
    },
    Deckungsanfrage: {
        acronym: 'DA',
        hints: 'Kennzeichnet E-Mails oder Briefe von Anwälten, in denen wortwörtlich die Anfrage nach Deckung gestellt wird.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Deckungszusage: {
        acronym: 'DZ',
        hints: 'Kennzeichnet Briefe oder E-Mail-Anhänge von Deckung-gebenden Versicherern, in denen eine Deckungszusage erteilt wird.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Eigenleistung: {
        acronym: 'EL',
        hints: 'Kennzeichnet E-Mails oder Anhänge, in denen dargelegt wird, dass in Eigenleistung repariert wird, und enthält üblicherweise eine Auflistung der vom Versicherungsnehmer erbrachten Arbeiten sowie eine Kostenaufstellung und detaillierte Arbeitszeiten, ohne dabei auf das neue Schaden Online Bezug zu nehmen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Tätigkeitsliste: {
                type: 'records',
                icon: faListOl,
                required: true,
                properties: {
                    Tätigkeit: { type: 'text' },
                    Stundensatz: {
                        type: 'number',
                        unit: 'EUR',
                        decimals: 2,
                    },
                    Stundenzahl: { type: 'number', unit: null, decimals: 'variable' },
                    Betrag: { type: 'number', unit: 'EUR', decimals: 2 },
                },
                hints: 'Liste der durchgeführten Arbeiten inkl. Beschreibung, Stundensatz, Arbeitsstunden und berechnetem Betrag.',
                summary: {
                    label: (value) => (value.length > 0 ? `TL ${value.length}` : false),
                    position: 1,
                },
            },
            Materialliste: {
                type: 'records',
                icon: faListUl,
                required: true,
                properties: {
                    Material: { type: 'text' },
                    Einheitspreis: { type: 'number', unit: 'EUR', decimals: 2 },
                    Menge: { type: 'number', unit: null, decimals: 'variable' },
                    Betrag: { type: 'number', unit: 'EUR', decimals: 2 },
                    // TODO: Confirm if ths requires an icon.
                    'Beleg vorhanden': {
                        type: 'choice',
                        options: [
                            ['Ja', 'Ja'],
                            ['Nein', 'Nein'],
                        ],
                        multi: false,
                    },
                },
                hints: 'Auflistung der verwendeten Materialien mit Angaben zu Bezeichnung, Einheitspreis, Menge, Betrag und Belegstatus.',
                summary: {
                    label: (value) => (value.length > 0 ? `ML ${value.length}` : false),
                    position: 2,
                },
            },
        },
    },
    Erinnerung: {
        acronym: 'ERI',
        hints: 'Kennzeichnet E-Mails oder Briefe, in denen der Versicherungsnehmer oder Vermittler an die Bearbeitung erinnert wird, in der Regel als Folge einer Sachstandsanfrage.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Erinnerungsgrund: {
                type: 'text',
                icon: faInputText,
                required: false,
                hints: 'Kurze Begründung, warum an die Bearbeitung erinnert wird.',
                summary: false,
            },
        },
    },
    Feuchtigkeitsmessprotokoll: {
        acronym: 'FMP',
        hints: 'Kennzeichnet häufig PDFs mit Fotos oder handschriftliche Formulare, die Messwerte zur Feststellung von Feuchtigkeitsschäden, beispielsweise nach einem Wasserschaden, dokumentieren.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Ergebnis: {
                type: 'choice',
                icon: faThumbsUp,
                required: true,
                options: [
                    ['ok', 'ok'],
                    ['nicht ok', 'n. ok'],
                ],
                multi: false,
                hints: false,
                summary: { label: ([_value, acronym]) => acronym, position: 1 },
            },
        },
    },
    Foto: {
        acronym: 'FOTO',
        hints: 'Kennzeichnet ein Schadenfoto, d.h. eine Einzelaufnahme zur Dokumentation eines Schadens. Ausgenommen hiervon sind Logos, Grafiken, Bilder die nur eine Unterschrift enthalten und Werbebilder - diese sind als Sonstiges einzuordnen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            'Beschädigte Sache': {
                type: 'text',
                icon: faCamera,
                required: false,
                hints: 'Sehr kurze und knappe Bezeichnung der beschädigten Sache oder des sichtbaren Objekts.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 1 },
            },
        },
    },
    Fotos: {
        acronym: 'FOTOS',
        hints: 'Kennzeichnet mehrere Schadenfotos, d.h. mehrere Aufnahmen zur Dokumentation eines Schadens, wobei Logos, Grafiken und Werbebilder nicht gemeint sind.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            'Beschädigte Sache': {
                type: 'text',
                icon: faCamera,
                required: false,
                hints: 'Sehr kurze und knappe Bezeichnung der beschädigten Sache oder des sichtbaren Objekts.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 1 },
            },
            'Anzahl Fotos': {
                type: 'number',
                icon: faTally,
                required: false,
                unit: null,
                decimals: 0,
                hints: 'Die Anzahl der Fotos, die im Dokument enthalten sind.',
                summary: false,
            },
        },
    },
    Gutachten: {
        acronym: 'GA',
        hints: 'Kennzeichnet in der Regel PDF-Anhänge, die als Gutachten oder Bericht bezeichnet werden und Prüfberichte zur Schadenursache, Schadenhöhe oder Regulierung enthalten. Es kann sein, dass ein Gutachter das Gutachten in mehrere Dokumente aufgeteilt einreicht, z.B. aufgeteilt in einen Bericht, ein Schätzungsdokument zur Schadenhöhe, eine Dokumentation zum Schaden und ggf. weitere Protokolle. Sofern solche Dokumente zusammenhängend von einem Gutachter eingereicht werden, sollen alle Dokumente zusammen als Gutachten eingeordnet werden.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Berichtsart: {
                type: 'choice',
                icon: faFileChartColumn,
                required: true,
                options: [
                    ['Vorbericht', 'Vb'],
                    ['Zwischenbericht', 'Zb'],
                    ['Prüfbericht', 'Pb'],
                    ['Abschlussbericht', 'Ab'],
                ],
                multi: false,
                hints: 'Art des Berichts, z.B. Vorbericht, Zwischenbericht, Prüfbericht oder Abschlussbericht.',
                summary: { label: ([_value, acronym]) => acronym, position: 1 },
            },
            Schadenhöhe: {
                type: 'number',
                icon: faEuroSign,
                required: true,
                unit: 'EUR',
                decimals: 2,
                hints: 'Ermittelte oder geschätzte Schadenhöhe in Euro.',
                summary: { label: (value) => formatEur(value), position: 2 },
            },
        },
    },
    'Kostenvoranschlag / Angebot': {
        acronym: 'KVA',
        hints: 'Kennzeichnet Dokumente, die als Kostenvoranschlag oder Angebot gekennzeichnet sind, während ein reines Anschreiben nicht gemeint ist. Kostenschätzungen zur Schadenhöhe im Rahmen von Gutachten und Berichten, sind als Gutachten einzuordnen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Status: {
                type: 'choice',
                icon: faListCheck,
                required: true,
                options: [
                    ['ungeprüft', 'ung'],
                    ['offen', 'off'],
                    ['freigegeben', 'frei'],
                    ['gekürzt freigegeben', 'gfrei'],
                    ['vorbehaltlich freigegeben', 'vfrei'],
                    ['gekürzt unter Vorbehalt freigegeben', 'gvfrei'],
                    ['bezahlt', 'bez'],
                    ['nicht ersatzpflichtig', 'ne'],
                    ['teilweise nicht ersatzpflichtig', 'tne'],
                    ['innerhalb Selbstbeteiligung', 'in SB'],
                ],
                multi: false,
                hints: false,
                summary: { label: ([_value, acronym]) => acronym, position: 1 },
            },
            Gewerke: {
                type: 'choice',
                icon: faScrewdriverWrench,
                required: true,
                options: [
                    ['Wiederherstellung', 'Whst'],
                    ['Leckortung', 'Lo'],
                    ['Behebung Ursache', 'Urs'],
                    ['Trocknung', 'Tro'],
                    ['Demontage', 'Dem'],
                    ['Entsorgung', 'Ent'],
                ],
                multi: true,
                hints: 'Liste der auf dem Kostenvoranschlag angegebenen Gewerke.',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 2,
                },
            },
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: true,
                unit: 'EUR',
                decimals: 2,
                hints: 'Gesamtkosten des Angebots bzw. Kostenvoranschlags, sofern der Versicherungsnehmer nicht vorsteuerabzugfähig ist, der Bruttobetrag, ansonsten der Nettobetrag',
                summary: { label: (value) => formatEur(value), position: 3 },
            },
            'Formelle Prüfung': {
                type: 'choice',
                icon: faTriangleExclamation,
                required: true,
                options: [
                    ['ok', 'ok'],
                    ['nicht ok', 'n. ok'],
                ],
                multi: false,
                hints: false,
                summary: { label: ([_value, acronym]) => acronym, position: 4 },
            },
            IBAN: {
                type: 'text',
                icon: faCreditCard,
                required: false,
                hints: 'IBAN des Kontos, auf das der Rechnungsbetrag überwiesen werden soll.',
                summary: false,
            },
            Rechnungssteller: {
                type: 'text',
                icon: faBuilding,
                required: false,
                hints: 'Juristisch korrekter Name des Rechnungsstellers.',
                summary: false,
            },
            Angebotsnummer: {
                type: 'text',
                icon: faFileInvoice,
                required: false,
                hints: 'Angebotsnummer, sofern vorhanden.',
                summary: false,
            },
            Angebotspositionen: {
                type: 'records',
                icon: faListOl,
                required: false,
                properties: {
                    Beschreibung: { type: 'text' },
                    'Betrag Netto': { type: 'number', unit: 'EUR', decimals: 2 },
                    'Betrag Brutto': { type: 'number', unit: 'EUR', decimals: 2 },
                    Steuersatz: { type: 'number', unit: '%', decimals: 0 },
                    Steuerbetrag: { type: 'number', unit: 'EUR', decimals: 2 },
                },
                hints: 'Liste der in dem Angebot angegebenen Angebotspositionen.',
                summary: false,
            },
        },
    },
    'Kostennote / Anwaltsrechnung': {
        acronym: 'KN',
        hints: 'Kennzeichnet Rechnungen von Anwälten oder Notaren, die spezifische juristische Leistungen mit detaillierten Abrechnungsinformationen dokumentieren.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Gesamtbetrag der Rechnung, sofern der Versicherungsnehmer nicht vorsteuerabzugfähig ist, der Bruttobetrag, ansonsten der Nettobetrag',
                summary: { label: (value) => formatEur(value), position: 1 },
            },
            Zahlstatus: {
                type: 'choice',
                icon: faMoneyBill,
                required: false,
                options: [
                    ['unbezahlt', 'unbez'],
                    ['bezahlt', 'bez'],
                ],
                multi: false,
                hints: false,
                summary: { label: ([_value, acronym]) => acronym, position: 2 },
            },
            IBAN: {
                type: 'text',
                icon: faCreditCard,
                required: false,
                hints: 'IBAN des Kontos, auf das der Rechnungsbetrag überwiesen werden soll.',
                summary: false,
            },
            Rechnungssteller: {
                type: 'text',
                icon: faBuilding,
                required: false,
                hints: 'Juristisch korrekter Name des Rechnungsstellers.',
                summary: false,
            },
            Rechnungsnummer: {
                type: 'text',
                icon: faFileInvoice,
                required: false,
                hints: 'Rechnungsnummer, sofern vorhanden.',
                summary: false,
            },
            Rechnungspositionen: {
                type: 'records',
                icon: faListOl,
                required: false,
                properties: {
                    Beschreibung: { type: 'text' },
                    'Betrag Netto': { type: 'number', unit: 'EUR', decimals: 2 },
                    'Betrag Brutto': { type: 'number', unit: 'EUR', decimals: 2 },
                    Steuersatz: { type: 'number', unit: '%', decimals: 0 },
                    Steuerbetrag: { type: 'number', unit: 'EUR', decimals: 2 },
                },
                hints: 'Liste der in der Rechnung angegebenen Rechnungspositionen.',
                summary: false,
            },
        },
    },
    Kulanzanfrage: {
        acronym: 'KULANZ',
        hints: 'Kennzeichnet E-Mails oder Briefe mit einer freundlichen Bitte um freiwillige Leistung, etwa unter dem Hinweis "ich bin doch schon lange Kunde" oder "ich decke sonst nicht mehr bei euch ein", auch wenn kein vertraglicher Anspruch besteht.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Leckortungsbericht: {
        acronym: 'LOB',
        hints: 'Kennzeichnet umfassende Berichte, die explizit als Leckortungsbericht, Ortungsbericht oder Leckageortung bezeichnet sind und die Aufdeckung von Leckagen dokumentieren, häufig unterstützt durch Kamerafahrten und Fotos.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            'Gefundene Ursache': {
                type: 'text',
                icon: faHouseCrack,
                required: false,
                hints: 'Beschreibung der gefundenen Ursache der Leckage.',
                summary: false,
            },
            Schadenhöhe: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Ermittelte Schadenhöhe im Zusammenhang mit dem Leckortungsbericht, sofern der Versicherungsnehmer nicht vorsteuerabzugfähig ist, der Bruttobetrag, ansonsten der Nettobetrag. Sofern die ermittelte Schadenhöhe als möglicher Bereich angegeben wird, soll der maximale Wert aus dem Bereich als Schadenhöhe angegeben werden.',
                summary: { label: (value) => formatEur(value), position: 1 },
            },
        },
    },
    Rechnung: {
        acronym: 'RG',
        hints: 'Kennzeichnet in der Regel PDFs, die als Rechnung, Schlussrechnung, Abrechnung, Zahlungsaufstellung, Quittung, Kassenbon oder Faktura bezeichnet werden, wobei ein reines Anschreiben nicht als Rechnung gilt, wenn die eigentliche Rechnung fehlt. Rechnungen zu Versicherungsbeiträgen bzw. Prämienrechnungen von Versicherern sind nicht als Rechnung, sondern als Abrechnung einzuordnen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Status: {
                type: 'choice',
                icon: faListCheck,
                required: true,
                options: [
                    ['ungeprüft', 'ung'],
                    ['offen', 'off'],
                    ['bezahlt', 'bez'],
                    ['teilweise nicht ersatzpflichtig', 'tne'],
                    ['nicht ersatzpflichtig', 'ne'],
                    ['innerhalb Selbstbeteiligung', 'in SB'],
                ],
                multi: false,
                hints: false,
                summary: { label: ([_value, acronym]) => acronym, position: 1 },
            },
            Gewerke: {
                type: 'choice',
                icon: faScrewdriverWrench,
                required: true,
                options: [
                    ['Wiederherstellung', 'Whst'],
                    ['Leckortung', 'Lo'],
                    ['Strom', 'St'],
                    ['Behebung Ursache', 'Urs'],
                    ['Trocknung', 'Tro'],
                    ['Demontage', 'Dem'],
                    ['Gutachten', 'Ga'],
                    ['Entsorgung', 'Ent'],
                    ['Schadenkosten', 'Schk'],
                ],
                multi: true,
                hints: 'Liste der in der Rechnung angegebenen Gewerke, z.B. Wiederherstellung, Leckortung etc.',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 2,
                },
            },
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: true,
                unit: 'EUR',
                decimals: 2,
                hints: 'Gesamtbetrag der Rechnung, sofern der Versicherungsnehmer nicht vorsteuerabzugfähig ist, der Bruttobetrag, ansonsten der Nettobetrag',
                summary: { label: (value) => formatEur(value), position: 3 },
            },
            // TODO: entfernen
            'innerhalb Selbstbeteiligung': {
                type: 'choice',
                icon: faSquare,
                required: false,
                options: [
                    ['Ja', 'Ja'],
                    ['Nein', 'Nein'],
                ],
                multi: false,
                hints: false,
                summary: {
                    label: ([value, _acronym]) => (value === 'Ja' ? 'in Sb' : false),
                    position: 4,
                },
            },
            'Formelle Prüfung': {
                type: 'choice',
                icon: faThumbsUp,
                required: true,
                options: [
                    ['ok', 'ok'],
                    ['nicht ok', 'n. ok'],
                ],
                multi: false,
                hints: false,
                summary: { label: ([_value, acronym]) => acronym, position: 5 },
            },
            IBAN: {
                type: 'text',
                icon: faCreditCard,
                required: false,
                hints: 'IBAN des Kontos, auf das der Rechnungsbetrag überwiesen werden soll.',
                summary: false,
            },
            Rechnungssteller: {
                type: 'text',
                icon: faBuilding,
                required: false,
                hints: 'Juristisch korrekter Name des Rechnungsstellers.',
                summary: false,
            },
            Rechnungsnummer: {
                type: 'text',
                icon: faFileInvoice,
                required: false,
                hints: 'Rechnungsnummer, sofern vorhanden.',
                summary: false,
            },
            Rechnungspositionen: {
                type: 'records',
                icon: faListOl,
                required: false,
                properties: {
                    Beschreibung: { type: 'text' },
                    'Betrag Netto': { type: 'number', unit: 'EUR', decimals: 2 },
                    'Betrag Brutto': { type: 'number', unit: 'EUR', decimals: 2 },
                    Steuersatz: { type: 'number', unit: '%', decimals: 0 },
                    Steuerbetrag: { type: 'number', unit: 'EUR', decimals: 2 },
                },
                hints: 'Liste der in der Rechnung angegebenen Rechnungspositionen.',
                summary: false,
            },
        },
    },
    'Regulierungsnachricht von VU': {
        acronym: 'RNVU',
        hints: 'Kennzeichnet standardisierte Briefe, E-Mails oder PDF-Anhänge von Deckung-gebenden Versicherern, in denen ein gezahlter Betrag genannt wird und die die Regulierung eines Schadens, beispielsweise bei Teilregulierung oder Ablehnung, anzeigen und als Zahlungsausgangsbestätigung dienen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Der in der Nachricht genannte Betrag, der als Zahlung bestätigt wird.',
                summary: { label: (value) => formatEur(value), position: 1 },
            },
            Gewerke: {
                type: 'choice',
                icon: faScrewdriverWrench,
                required: false,
                options: [
                    ['Wiederherstellung', 'Whst'],
                    ['Leckortung', 'Lo'],
                    ['Strom', 'St'],
                    ['Eigenleistung', 'El'],
                    ['Behebung Ursache', 'Urs'],
                    ['Trocknung', 'Tro'],
                ],
                multi: true,
                hints: 'Gewerke, zu denen Zahlung geleistet wurde.',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 2,
                },
            },
        },
    },
    Rückruf: {
        acronym: 'RR',
        hints: 'Kennzeichnet PDF-Formulare oder Notizen, in denen um einen Rückruf gebeten wird, wobei das Formular als Rückrufanforderung dient.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'expected' },
        properties: {
            Name: {
                type: 'text',
                icon: faInputText,
                required: false,
                hints: 'Name der Person, die zurückgerufen werden soll.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 1 },
            },
            Rückrufgrund: {
                type: 'text',
                icon: faPhoneArrowRight,
                required: false,
                hints: 'Grund oder Anliegen, das den Rückruf erforderlich macht.',
                summary: false,
            },
            TODO: {
                type: 'text',
                icon: faRectangleList,
                required: false,
                hints: 'Aufgabe des Sachbearbeiters.',
                summary: false,
            },
        },
    },
    Sachstandsanfrage: {
        acronym: 'SSA',
        hints: 'Kennzeichnet Briefe oder E-Mails, in denen nach dem aktuellen Bearbeitungsstand eines Schadenfalls gefragt wird, etwa ob bereits gezahlt wurde oder Unterlagen fehlen, und unterscheidet sich damit von einer reinen Erinnerung.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Schadenaufstellung: {
        acronym: 'SAUF',
        hints: 'Kennzeichnet Dokumente, die alle Kosten und Aufwände, einschließlich Eigenleistungen, gezahlter Rechnungen und Energieverbräuchen, detailliert auflisten und Angaben zu betroffenen Gegenständen oder Leistungen enthalten.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Tätigkeitsliste: {
                type: 'records',
                icon: faListOl,
                required: false,
                properties: {
                    Tätigkeit: { type: 'text' },
                    Stundensatz: {
                        type: 'number',
                        unit: 'EUR',
                        decimals: 2,
                    },
                    Stundenzahl: { type: 'number', unit: null, decimals: 'variable' },
                    Betrag: { type: 'number', unit: 'EUR', decimals: 2 },
                },
                hints: 'Liste der erfassten Tätigkeiten inkl. Beschreibung, Stundensatz, Arbeitsstunden und berechnetem Betrag.',
                summary: {
                    label: (value) => (value.length > 0 ? `TL ${value.length}` : false),
                    position: 1,
                },
            },
            Materialliste: {
                type: 'records',
                icon: faListUl,
                required: false,
                properties: {
                    Material: { type: 'text' },
                    Einheitspreis: { type: 'number', unit: 'EUR', decimals: 2 },
                    Menge: { type: 'number', unit: null, decimals: 'variable' },
                    Betrag: { type: 'number', unit: 'EUR', decimals: 2 },
                    'Beleg vorhanden': {
                        type: 'choice',
                        options: [
                            ['Ja', 'Ja'],
                            ['Nein', 'Nein'],
                        ],
                        multi: false,
                    },
                },
                hints: 'Liste der verwendeten Materialien mit Angaben zu Bezeichnung, Einheitspreis, Menge, Betrag und Belegstatus.',
                summary: {
                    label: (value) => (value.length > 0 ? `ML ${value.length}` : false),
                    position: 2,
                },
            },
            Stromverbrauch: {
                type: 'records',
                icon: faPlug,
                required: false,
                properties: {
                    Menge: { type: 'number', unit: 'kWh', decimals: 'variable' },
                    Einheitspreis: { type: 'number', unit: 'EUR', decimals: 2 },
                    Betrag: { type: 'number', unit: 'EUR', decimals: 2 },
                },
                hints: 'Aufstellung des Stromverbrauchs inklusive Menge, Einheitspreis und berechnetem Betrag.',
                summary: {
                    label: (value) => (value.length > 0 ? `ST ${value.length}` : false),
                    position: 3,
                },
            },
            'Liste weiterer Kosten': {
                type: 'records',
                icon: faFileInvoiceDollar,
                required: false,
                properties: {
                    Bezeichnung: { type: 'text' },
                    Betrag: { type: 'number', unit: 'EUR', decimals: 2 },
                },
                hints: 'Liste zusätzlicher Kostenpositionen mit Bezeichnung und Betrag.',
                summary: {
                    label: (value) => (value.length > 0 ? `W ${value.length}` : false),
                    position: 4,
                },
            },
        },
    },
    Schadenmeldung: {
        acronym: 'SM',
        hints: 'Kennzeichnet Schadenmelde-Formulare als Dokument oder E-Mail, E-Mails zu Meldungen über Online-Schadenformulare, E-Mails zu Schadenmeldungen über den Sprachbot Parloa, E-Mails zu Schadenmeldungen über Chatbots, formlose E-Mails vom Versicherungsnehmer oder Vermittler in denen ein neuer Schaden gemeldet wird, als auch Dokumente die als maschinelle Schadenmeldung (Erstmeldung) bezeichnet werden. Ausgenommen hiervon sind Eingangsbestätigungen, die lediglich den Eingang einer Schadenmeldung bestätigen und die Schadennummer bekanntgeben.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Schadenhöhe: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Geschätzte oder gemeldete Schadenhöhe in Euro.',
                summary: {
                    label: (value) => formatEur(value),
                    position: 1,
                },
            },
        },
    },
    Sanierungsunterlagen: {
        acronym: 'SUN',
        hints: 'Kennzeichnet in der Regel Rechnungen, die die Wiederherstellung eines baulichen Mangels oder eine Modernisierung dokumentieren, insbesondere wenn sie älter als das Schadensdatum sind.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Sanierungsleistung: {
                type: 'text',
                icon: faRepeat,
                required: false,
                hints: 'Beschreibung der durchgeführten Sanierungs- oder Wiederherstellungsarbeiten.',
                summary: false,
            },
        },
    },
    Video: {
        acronym: 'VIDEO',
        hints: 'Kennzeichnet Videodateien, die zur Dokumentation eines Schadens oder anderer relevanter Informationen dienen.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Widerspruch: {
        acronym: 'WS',
        hints: 'Kennzeichnet Widersprüche gegen Entscheidungen von Deckung-gebenden Versicherern und stellt einen formellen Einspruch gegen eine Schadenentscheidung oder Regulierung dar.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    'Zahlungserinnerung / Mahnung': {
        acronym: 'ZE',
        hints: 'Kennzeichnet Zahlungserinnerungen oder Mahnungen, die meist von Handwerkern verschickt werden, um an ausstehende Zahlungen zu erinnern, sofern der Versicherungsnehmer noch nicht gezahlt hat.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Offener Betrag, der in der Erinnerung oder Mahnung zur Zahlung aufgeführt ist.',
                summary: { label: (value) => formatEur(value), position: 1 },
            },
        },
    },
    AGB: {
        acronym: 'AGB',
        hints: 'Kennzeichnet Allgemeine Geschäftsbedingungen von Dienstleistern, wobei Versicherungsbedingungen ausdrücklich ausgeschlossen sind.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Rückläufer: {
        acronym: 'RL',
        hints: 'Kennzeichnet Briefe, die als Rückläufer gelten – etwa weil sie unbekannt oder verzogen sind – oder E-Mail-Unzustellbarkeitsnachrichten, die im Betreff Hinweise wie "Unzustellbar" enthalten, wenn E-Mails nicht zugestellt werden können.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    'Versicherer Schreiben': {
        acronym: 'VUINFO',
        hints: 'Kennzeichnet alle eingehenden E-Mails und Schreiben von Deckung-gebenden Versicherern, ausgenommen davon sind Regulierungsnachrichten. Ausgehende E-Mails und Schreiben sind keine Versicherer Schreiben.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Irrläufer: {
        acronym: 'IRR',
        hints: false,
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    // AUSGANG
    Ablehnung: {
        acronym: 'ABL',
        hints: 'Kennzeichnet E-Mails oder Briefe mit Standardtextblöcken, beispielsweise "nicht ersatzpflichtig", die eine Ablehnung der Schadenregulierung ausdrücken. Formulierungen wie "nicht versichert", "nicht ersatzpflichtig", oder auch "nicht vom Versicherungsschutz umfasst" können auf eine Ablehnung hindeuten.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Grund: {
                type: 'text',
                icon: faXmarkToSlot,
                required: false,
                hints: 'Begründung der Ablehnung, z.B. Nicht-Ersatzpflichtigkeit.',
                summary: false,
            },
        },
    },
    Abrechnung: {
        acronym: 'VNA',
        hints: 'Kennzeichnet standardisierte PDFs zur Schadenauszahlung oder auch Abrechnung von Versicherungsbeiträgen. Häufig sind die Dokumente durch eine Abrechnungsnummer mit dem Präfix "VNA" gekennzeichnet.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Gewerke: {
                type: 'choice',
                icon: faScrewdriverWrench,
                required: true,
                options: [
                    ['Wiederherstellung', 'Whst'],
                    ['Leckortung', 'Lo'],
                    ['Strom', 'St'],
                    ['Eigenleistung', 'Eigen'],
                    ['Behebung Ursache', 'Urs'],
                    ['Trocknung', 'Tro'],
                ],
                multi: true,
                hints: 'Liste der beteiligten Gewerke (mehrere Auswahl möglich).',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 1,
                },
            },
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Gesamtabrechnungsbetrag, wie im Abrechnungsschreiben aufgeführt.',
                summary: { label: (value) => formatEur(value), position: 2 },
            },
            'VNA-Nummer': {
                type: 'text',
                icon: faInputNumeric,
                required: false,
                hints: 'Eindeutige Abrechnungsnummer mit dem Präfix "VNA".',
                summary: { label: (value) => value, position: 3 },
            },
        },
    },
    'Anforderung Unterlagen': {
        acronym: 'ULA',
        hints: 'Kennzeichnet in der Regel Standard-E-Mails – ob von Mensch oder KI – in denen weitere Unterlagen und Informationen angefordert werden, um fehlende oder zusätzliche Dokumente zu einem Schadenfall zu ergänzen. Fordert der Deckung-gebende Versicherer weitere Unterlagen an, dann ist die Kategorie "Versicherer Schreiben" zu wählen.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    'Antwort auf Widerspruch': {
        acronym: 'AWS',
        hints: 'Kennzeichnet Briefe oder E-Mails, die als Antwort auf einen eingegangenen Widerspruch dienen.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Inhalt: {
                type: 'text',
                icon: faArrowsToCircle,
                required: false,
                hints: 'Inhaltliche Antwort oder Stellungnahme auf den Widerspruch.',
                summary: false,
            },
        },
    },
    Freigabe: {
        acronym: 'FREI',
        hints: 'Kennzeichnet ausgehende Briefe oder E-Mails mit standardisierten Textblöcken, in denen ein Kostenvoranschlag oder Angebot zur Ausführung freigegeben wird. Werden zusätzlich Fotos oder Messprotokolle angefordert, oder auch die Einreichung einer Rechnung angeführt, oder sogar die Formulierung "unter Vorbehalt" oder "vorbehaltlich" verwendet, ist die Kategorie "Vorbehaltliche Freigabe" zu wählen.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Gewerke: {
                type: 'choice',
                icon: faScrewdriverWrench,
                required: true,
                options: [
                    ['Wiederherstellung', 'Whst'],
                    ['Leckortung', 'Lo'],
                    ['Strom', 'St'],
                    ['Eigenleistung', 'Eigen'],
                    ['Behebung Ursache', 'Urs'],
                    ['Trocknung', 'Tro'],
                ],
                multi: true,
                hints: 'Liste der beteiligten Gewerke (mehrere Auswahl möglich).',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 1,
                },
            },
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Zugelassener Kostenbetrag, der in der Freigabe bestätigt wird.',
                summary: { label: (value) => formatEur(value), position: 2 },
            },
        },
    },
    'Information zur Abgabe GSM': {
        acronym: 'IGSM',
        hints: 'Kennzeichnet E-Mails an Versicherungsnehmer oder Vermittler, die darüber informieren, dass die Schadenbearbeitung aufgrund der Schadenhöhe an den Deckung-gebenden Versicherer abgegeben wird.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    Regressforderung: {
        acronym: 'RF',
        hints: 'Kennzeichnet ausgehende Dokumente und E-Mails, in denen von Handwerkern, Versicherern, Versicherungsnehmern oder Geschädigten die Erstattung bereits geleisteter Zahlungen gefordert werden, oder über eine Regressnahme informiert wird. Begriffe wie Regressforderung, Regressansprüche und Regressnahme können auf eine Regressforderung hindeuten.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Art: {
                type: 'choice',
                icon: faRepeat,
                required: true,
                options: [
                    ['interne Verrechnung', 'iV'],
                    ['Regress', 'Reg'],
                    ['Teilung', 'Teil'],
                    ['Doppelversicherung', 'Doppel'],
                    ['Rückforderung', 'Rück'],
                ],
                multi: false,
                hints: false,
                summary: {
                    label: ([_value, acronym]) => acronym,
                    position: 1,
                },
            },
        },
    },
    Regulierungsnachricht: {
        acronym: 'RN',
        hints: 'Kennzeichnet E-Mails oder Briefe, in denen ein zu zahlender Betrag genannt wird, was auf die Regulierung eines Schadens hinweist – häufig mit VNA-Anhängen, jedoch ohne dass eine formelle Abrechnung erfolgt.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Gewerke: {
                type: 'choice',
                icon: faScrewdriverWrench,
                required: true,
                options: [
                    ['Wiederherstellung', 'Whst'],
                    ['Leckortung', 'Lo'],
                    ['Strom', 'St'],
                    ['Eigenleistung', 'Eigen'],
                    ['Behebung Ursache', 'Urs'],
                    ['Trocknung', 'Tro'],
                ],
                multi: true,
                hints: 'Liste der beteiligten Gewerke (mehrere Auswahl möglich).',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 1,
                },
            },
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Der auszuzahlende Betrag, wie im Schreiben angegeben.',
                summary: { label: (value) => formatEur(value), position: 2 },
            },
        },
    },
    Teilablehnung: {
        acronym: 'TABL',
        hints: 'Kennzeichnet E-Mails oder Briefe mit Standardtextblöcken wie "Ursache nicht versichert, Folge doch versichert", die auch Zahlungsinformationen enthalten können und vorrangig vor Regulierungsnachricht und Ablehnung eingeordnet werden.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Grund: {
                type: 'text',
                icon: faShieldExclamation,
                required: false,
                hints: 'Begründung der Teilablehnung, z.B. spezifische Ausschlüsse oder Einschränkungen.',
                summary: false,
            },
        },
    },
    'Vorbehaltliche Freigabe': {
        acronym: 'VFREI',
        hints: 'Kennzeichnet ausgehende Dokumente, die einer Freigabe ähneln, jedoch mit der Anforderung weiterer Unterlagen verbunden sind, wie z.B. die Anforderung von Fotos oder Messprotokolle, oder auch den Hinweis, dass eine Rechnung noch einzureichen ist. Auch die Formulierung "unter Vorbehalt" oder "vorbehaltlich" im Rahmen einer Freigabe können auf eine Vorbehaltliche Freigabe hindeuten.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {
            Gewerke: {
                type: 'choice',
                icon: faScrewdriverWrench,
                required: true,
                options: [
                    ['Wiederherstellung', 'Whst'],
                    ['Leckortung', 'Lo'],
                    ['Strom', 'St'],
                    ['Eigenleistung', 'Eigen'],
                    ['Behebung Ursache', 'Urs'],
                    ['Trocknung', 'Tro'],
                ],
                multi: true,
                hints: 'Liste der betroffenen Gewerke, deren Freigabe unter Vorbehalt erfolgt.',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 1,
                },
            },
            Betrag: {
                type: 'number',
                icon: faEuroSign,
                required: false,
                unit: 'EUR',
                decimals: 2,
                hints: 'Der vorläufig genehmigte Betrag, der unter Vorbehalt freigegeben wird.',
                summary: { label: (value) => formatEur(value), position: 2 },
            },
        },
    },
    'Abgabe Gutachter': {
        acronym: 'ABGA',
        hints: 'Kennzeichnet Schreiben und E-Mails, die formell oder informell einen Prüfauftrag an einen Gutachter oder Experten erteilen. Formulierungen wie "Bitte nehmen Sie eine Prüfung vor" oder "senden wir Ihnen zur Prüfung zu" können auf eine Abgabe an einen Gutachter hindeuten.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    Erstabgabe: {
        acronym: 'EAB',
        hints: 'Kennzeichnet standardisierte E-Mails, Briefe oder Mitteilungen, die an den Deckung-gebenden Versicherer gerichtet sind, die erstmals einen eingetretenen Schaden melden und die Bearbeitung an den Deckung-gebenden Versicherer übergeben.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    Folgeabgabe: {
        acronym: 'FAB',
        hints: 'Kennzeichnet standardisierte E-Mails, Briefe oder Mitteilungen, die an den Deckung-gebenden Versicherer gerichtet sind, die Änderungen oder zusätzliche Informationen zu einen bereits gemeldeten Schaden enthalten, so dass der Deckung-gebende Versicherer die Bearbeitung des Schadensfalls fortsetzen kann.',
        flow: { inbound: 'maybe', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    // EINGANG und AUSGANG
    Schweigepflichtsentbindung: {
        acronym: 'SSE',
        hints: 'Kennzeichnet Dokumente zur Schweigepflichtsentbindung, die eine Erlaubnis zur Weitergabe von vertraulichen Informationen an Dritte enthalten.',
        flow: { inbound: 'expected', outbound: 'expected', internal: 'maybe' },
        properties: {
            Versicherer: {
                type: 'text',
                icon: faBuilding,
                required: false,
                hints: 'Name des Versicherungsunternehmens, für das die Schweigepflichtsentbindung erteilt wird.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 1 },
            },
        },
    },
    Schadeninfo: {
        acronym: 'SI',
        hints: 'Kennzeichnet informelle und auch standardisierte Schadeninformationsschreiben, die weder als Regulierungsnachricht noch als Schadenmeldung, noch als Versicherer Schreiben gelten, sondern über den Bearbeitungsstand oder den Fortschritt des Schadensfalls informieren. Dies umfasst auch Folgemeldungen und auch standardisierte PDFs, die an Vermittler gesendet werden, um über den Bearbeitungsstand und relevante Details eines Schadenfalls zu informieren.',
        flow: { inbound: 'expected', outbound: 'expected', internal: 'maybe' },
        properties: {
            Kerninfo: {
                type: 'text',
                icon: faCircleInfo,
                required: false,
                hints: 'Wesentliche Informationen und Details zum Schadenfall, die für die Bearbeitung relevant sind.',
                summary: false,
            },
        },
    },
    Eingangsbestätigung: {
        acronym: 'EB',
        hints: 'Kennzeichnet schriftliche Bestätigungen des Eingangs eines Schreibens oder einer E-Mail, die keinen weiteren inhaltlichen Informationsgehalt aufweisen.',
        flow: { inbound: 'expected', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    Brief: {
        acronym: 'BRIEF',
        hints: 'Kennzeichnet klassische Anschreiben in Briefform, die keiner anderen spezifischeren Kategorie zugeordnet werden können.',
        flow: { inbound: 'expected', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    'E-Mail': {
        acronym: 'EMAIL',
        hints: 'Kennzeichnet E-Mails, die keiner anderen spezifischeren Kategorie zugeordnet werden können.',
        flow: { inbound: 'expected', outbound: 'expected', internal: 'maybe' },
        properties: {},
    },
    // INTERN
    Deckungsprüfung: {
        acronym: 'DP',
        hints: 'Kennzeichnet Dokumente, die eindeutig als "Deckungsprüfung" gekennzeichnet sind und den Umfang des Versicherungsschutzes überprüfen.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'expected' },
        properties: {},
    },
    'Abgabe GSM': {
        acronym: 'GSM',
        hints: 'Kennzeichnet interne Notizen, die die Abgabe der Schadenbearbeitung an das deckunggebende Versicherungsunternehmen fordern oder um die Behandlung als Großschaden bitten, wobei häufig Assekuradeure eingeschaltet werden.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'expected' },
        properties: {},
    },
    Notiz: {
        acronym: 'NTZ',
        hints: 'Kennzeichnet interne Word-Dokumente oder handschriftliche Notizen, die zu einem Telefonat oder rein internen Vorgängen dienen, wobei Rückrufbitten ausgeschlossen sind.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'expected' },
        properties: {},
    },
    Wetterprüfung: {
        acronym: 'WP',
        hints: 'Kennzeichnet standardisierte Protokolle, die den tatsächlichen Wetterverlauf an einem bestimmten Ort und zu einer bestimmten Zeit dokumentieren und zur Beurteilung der Schadenursache dienen, wobei zukünftig Meteomatics-Daten einfließen sollen.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'expected' },
        properties: {
            Ergebnis: {
                type: 'choice',
                icon: faWind,
                required: true,
                options: [
                    ['positiv', 'pos'],
                    ['negativ', 'neg'],
                ],
                multi: false,
                hints: 'Ergebnis der Wetterprüfung, z.B. ob das Wetterereignis als ursächlich für den Schaden bewertet wird.',
                summary: {
                    label: ([_value, acronym]) => acronym,
                    position: 1,
                },
            },
            Prüfart: {
                type: 'choice',
                // TODO: confirm if to repeat icon
                icon: faWind,
                required: false,
                options: [
                    ['Niederschlagsmenge', 'Niederschlag'],
                    ['Sturm', 'Sturm'],
                    ['Blitz', 'Blitz'],
                    ['Gewitter', 'Gewitter'], // TODO: entfernen und bereinigen
                    ['Hagel', 'Hagel'],
                ],
                multi: true,
                hints: 'Art der Wetterprüfung, z.B. Niederschlagsmenge, Sturm, Blitz oder Hagel (mehrere Optionen möglich).',
                summary: {
                    label: (value) => value.map(([_, acronym]) => acronym).join(' '),
                    position: 2,
                },
            },
        },
    },
    Belegprüfung: {
        acronym: 'BP',
        hints: 'Kennzeichnet ein Prüfungsprotokoll zu einer technischen Belegprüfung und kaufmännischen Nachkalkulation, die üblicherweise eine Regulierungsempfehlung enthält. Häufig von PropertyExpert erstellt.',
        flow: { inbound: 'expected', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Ampelstatus: {
                type: 'choice',
                icon: faTrafficLight,
                required: true,
                options: [
                    ['Rot', 'Rot'],
                    ['Gelb', 'Gelb'],
                    ['Grün', 'Grün'],
                ],
                multi: false,
                hints: 'Ampelstatus der Belegprüfung, z.B. Rot, Gelb oder Grün.',
                summary: { label: ([_value, acronym]) => acronym, position: 1 },
            },
            Firmenname: {
                type: 'text',
                icon: faInputText,
                required: true,
                hints: 'Name des Unternehmens oder der Firma, die den Beleg ausgestellt hat.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 2 },
            },
            Belegnummer: {
                type: 'text',
                icon: faInputText,
                required: true,
                hints: 'Nummer des Beleges, z.B. Rechnungsnummer, Nummer des Kostenvoranschlags, etc.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 3 },
            },
            Originalbetrag: {
                type: 'number',
                icon: faEuroSign,
                required: true,
                unit: 'EUR',
                decimals: 2,
                hints: 'Originalbetrag des Beleges in Euro.',
                summary: { label: (value) => formatEur(value), position: 4 },
            },
            Regulierungsempfehlung: {
                type: 'number',
                icon: faCircleEuro,
                required: true,
                unit: 'EUR',
                decimals: 2,
                hints: 'Empfehlung für die Regulierung des Beleges in Euro.',
                summary: { label: (value) => formatEur(value), position: 5 },
            },
        },
    },
    // WEITERE
    Versicherungsbedingungen: {
        acronym: 'VB',
        hints: 'Kennzeichnet umfassende Versicherungsbedingungen oder Vertragsgrundlagen, die den Versicherungsschutz regeln.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Versicherer: {
                type: 'text',
                icon: faBuilding,
                required: false,
                hints: 'Name des Versicherungsunternehmens, das die Bedingungen ausstellt.',
                summary: { label: (value) => value.substring(0, 10).trim(), position: 1 },
            },
        },
    },
    Police: {
        acronym: 'POL',
        hints: 'Kennzeichnet Dokumente, die als Police oder Versicherungsschein gelten und vertragliche Details zum Versicherungsschutz enthalten.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Vertragsnummer: {
                type: 'text',
                icon: faRadar,
                required: false,
                hints: 'Vertrags- oder Police-Nummer zur Identifikation des Versicherungsscheins.',
                summary: { label: (value) => value, position: 1 },
            },
        },
    },
    Duplikat: {
        acronym: 'DUPL',
        hints: false,
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Archiv: {
        acronym: 'AR',
        hints: 'Kennzeichnet reine Archivformate ohne weiteren eigenen Informationsgehalt, wie beispielsweise ZIP- und RAR-Dateien, die typischerweise die Dateiendungen .zip, .rar, .7z, .tar, .tar.gz haben.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    Sonstiges: {
        acronym: 'SON',
        hints: 'Kennzeichnet Dokumente, die keiner der anderen fest definierten Kategorien eindeutig zugeordnet werden können.',
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'maybe' },
        properties: {
            Kerninfo: {
                type: 'text',
                icon: faCircleInfo,
                required: false,
                hints: 'Kerninformationen des Dokuments.',
                summary: false,
            },
        },
    },
    // Only used for the virtual Vaarhaft analysis report; hints: false keeps it out of AI classification.
    Betrugsanalyse: {
        acronym: 'BA',
        hints: false,
        flow: { inbound: 'maybe', outbound: 'maybe', internal: 'maybe' },
        properties: {},
    },
    // VERALTET
    Informationsblatt: 'Sonstiges',
    Kostenvoranschlag: 'Kostenvoranschlag / Angebot',
    Schadenanzeige: 'Schadenmeldung',
    Schadenfoto: 'Foto',
    Schadenfotos: 'Fotos',
    Schadeninformation: 'Schadeninfo',
    'Schadeninfo an VM': 'Schadeninfo',
    Schadenvideo: 'Video',
    Unzustellbarkeitsnachricht: 'Rückläufer',
    Zahlungserinnerung: 'Zahlungserinnerung / Mahnung',
    Neuschadenmeldung: 'Erstabgabe',
    Schadenfolgemeldung: 'Folgeabgabe',
};
export const DocumentTypeValues = Object.keys(DocumentClassificationConfig);
export const DocumentTypeEnum = zEnumFromArray(DocumentTypeValues);
export const PreferredDocumentTypeValues = Object.keys(_.pickBy(DocumentClassificationConfig, (config) => typeof config !== 'string'));
export const DocumentTypeAcronymMap = Object.fromEntries(DocumentTypeValues.map((type) => {
    let config = DocumentClassificationConfig[type];
    if (typeof config === 'string') {
        config = DocumentClassificationConfig[config];
    }
    return [type, config.acronym];
}));
export const DocumentTypeSortIndicator = [
    'Anwalts- / Gerichtsschreiben',
    'Beschwerde',
    'Widerspruch',
    'Rückruf',
    'Rechnung',
    'Gutachten',
    'Kostenvoranschlag / Angebot',
    'Kostenvoranschlag', // outdated
    'Kulanzanfrage',
    'Leckortungsbericht',
    'Feuchtigkeitsmessprotokoll',
    'Fotos',
    'Schadenfotos', // outdated
    'Foto',
    'Schadenfoto', // outdated
    'Asservat',
    'Schadeninfo',
    'Schadeninformation', // outdated
    'Sachstandsanfrage',
];
export const KommunikationsflussValues = ['Eingang', 'Ausgang', 'Intern', 'Extern', 'Unbestimmt'];
/**
 * Who is on the other side of a Hauptdokument: its sender when it came in, its recipient when it went out.
 * One category per role a Kontakt of the claim can carry, in the order of the roles; two are spelled
 * differently there — `Versicherungsunternehmen` is the role Versicherer, `Sonstiger` the role Sonstige.
 */
export const PartnerKategorieValues = [
    'Versicherungsnehmer',
    'Hausverwaltung',
    'Vertreter',
    'Vermittler',
    'Untervermittler',
    'Versicherungsunternehmen',
    'Fremdversicherer',
    'Mieter',
    'Geschädigter',
    'Verursacher',
    'Handwerker',
    'Gutachter',
    'Anwalt',
    'Sonstiger',
];
/** The short form of each category, which the OASIS subject of a document and the claimdesk badge show. */
export const PartnerKategorieAcronymMap = {
    Versicherungsnehmer: 'VN',
    Hausverwaltung: 'HV',
    Vertreter: 'VT',
    Vermittler: 'VM',
    Untervermittler: 'UVM',
    Versicherungsunternehmen: 'VU',
    Fremdversicherer: 'FVU',
    Mieter: 'MI',
    Geschädigter: 'GES',
    Verursacher: 'VUR',
    Handwerker: 'HW',
    Gutachter: 'GA',
    Anwalt: 'ANW',
    Sonstiger: 'SON',
};
/**
 * Represents the details of a document.
 */
export function getDocumentClassificationConfig(documentType) {
    const config = DocumentClassificationConfig[documentType];
    if (typeof config === 'string') {
        return DocumentClassificationConfig[config];
    }
    return config;
}
export function getDocumentClassificationPropertiesConfig(documentType) {
    const config = getDocumentClassificationConfig(documentType);
    return {
        ...config.properties,
        ...DocumentClassificationCommonPropertiesConfig,
    };
}
export function createDocumentCategoryPropertiesSchema(category, purpose) {
    const propertiesConfig = getDocumentClassificationPropertiesConfig(category);
    const shape = {};
    for (const [propertyName, propertyConfig] of Object.entries(propertiesConfig)) {
        if (purpose === 'ai-scan' && propertyConfig.hints === false) {
            continue;
        }
        switch (propertyConfig.type) {
            case 'text':
                shape[propertyName] = z.union([z.string(), z.null()]);
                break;
            case 'choice':
                {
                    const enumSchema = zEnumFromArray(propertyConfig.options.map(([value, _acronym]) => value));
                    shape[propertyName] = z.union([propertyConfig.multi ? enumSchema.array() : enumSchema, z.null()]);
                }
                break;
            case 'date':
                shape[propertyName] = z.union([
                    z.strictObject({
                        year: z.number(),
                        month: z.number(),
                        day: z.number(),
                    }),
                    z.null(),
                ]);
                break;
            case 'number':
                shape[propertyName] = z.union([z.number(), z.null()]);
                break;
            case 'records':
                {
                    const recordShape = {};
                    for (const [recordPropName, recordPropConfig] of Object.entries(propertyConfig.properties)) {
                        switch (recordPropConfig.type) {
                            case 'text':
                                recordShape[recordPropName] = z.union([z.string(), z.null()]);
                                break;
                            case 'number':
                                recordShape[recordPropName] = z.union([z.number(), z.null()]);
                                break;
                            case 'choice':
                                {
                                    const enumSchema = zEnumFromArray(recordPropConfig.options.map(([value, _acronym]) => value));
                                    recordShape[recordPropName] = z.union([
                                        recordPropConfig.multi ? enumSchema.array() : enumSchema,
                                        z.null(),
                                    ]);
                                }
                                break;
                            default:
                                throw new Error(`Unsupported property type: ${recordPropConfig.type}`);
                        }
                    }
                    shape[propertyName] = z.union([z.array(z.object(recordShape)), z.null()]);
                }
                break;
            default:
                throw new Error(`Unsupported property type: ${propertyConfig.type}`);
        }
    }
    if (purpose === 'storage' || purpose === 'update') {
        return z.object(shape).partial();
    }
    return z.object(shape);
}
export function createDocumentPropertiesSchema(purpose) {
    const shape = {};
    for (const category of DocumentTypeValues) {
        shape[category] = createDocumentCategoryPropertiesSchema(category, purpose);
    }
    return z.object(shape);
}
export const DocumentDetails = createDocumentPropertiesSchema('storage').extend({
    Kommunikationsfluss: z.enum(KommunikationsflussValues),
    'Partner Kategorie': z.enum(PartnerKategorieValues),
    'Partner Name': z.string().min(1),
    Dokumentenart: DocumentTypeEnum.array(),
    // OUTDATED
    Kurzbeschreibung: z.unknown(),
    Schlagworte: z.unknown(),
    Auftragnehmer: z.unknown(),
    'Gewerk/Sache': z.unknown(),
    'Betrag EUR': z.unknown(),
    Rechnungspositionen: z.unknown(),
    Buchungskategorie: z.unknown(),
    'Aufnahme Ort/Raum': z.unknown(),
    'Aufnahme Detailgrad': z.unknown(),
    Dokumentendatum: z.unknown(),
});
